import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { assetsAPI } from '@/lib/api';
import { cn, getStatusColor, formatDate } from '@/lib/utils';
import { 
  Search, Server, Monitor, Wifi, Cloud, Shield, RefreshCw, MoreVertical,
  Cpu, HardDrive, Thermometer, Clock, MapPin, Tag, Activity, AlertTriangle,
  Network, Info, Settings, X, Terminal
} from 'lucide-react';
import { CiscoTerminal } from '@/components/CiscoTerminal';

const typeIcons = {
  switch: Wifi,
  router: Wifi,
  firewall: Shield,
  server: Server,
  endpoint: Monitor,
  iot: Shield,
  cloud: Cloud,
};

export function AssetsPage() {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [terminalAsset, setTerminalAsset] = useState(null);

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    filterAssets();
  }, [assets, searchTerm, typeFilter, statusFilter]);

  const loadAssets = async () => {
    try {
      const response = await assetsAPI.getAll();
      setAssets(response.data.assets);
    } catch (error) {
      console.error('Error loading assets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAssets = () => {
    let filtered = [...assets];
    
    if (searchTerm) {
      filtered = filtered.filter(a => 
        a.hostname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.ip_addresses.some(ip => ip.includes(searchTerm)) ||
        a.vendor.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => a.type === typeFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }
    
    setFilteredAssets(filtered);
  };

  const assetTypes = [...new Set(assets.map(a => a.type))];

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return `${days}d ${hours}h`;
  };

  return (
    <div className="min-h-screen" data-testid="assets-page">
      <Header title="Assets" />
      
      <div className="p-6 space-y-6">
        {/* Filters */}
        <Card className="bg-card/50 border-border">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por hostname, IP, vendor..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="asset-search"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]" data-testid="type-filter">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {assetTypes.map(type => (
                    <SelectItem key={type} value={type}>{type.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadAssets} data-testid="refresh-assets">
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(
            filteredAssets.reduce((acc, a) => {
              acc[a.type] = (acc[a.type] || 0) + 1;
              return acc;
            }, {})
          ).map(([type, count]) => {
            const Icon = typeIcons[type] || Server;
            return (
              <Card 
                key={type} 
                className="bg-card/50 border-border hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => setTypeFilter(type)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">{type}</p>
                      <p className="text-xl font-mono font-bold">{count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Table */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
              Inventário de Ativos ({filteredAssets.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Hostname</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Modelo</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Health</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.map((asset) => {
                    const Icon = typeIcons[asset.type] || Server;
                    return (
                      <TableRow 
                        key={asset.id} 
                        className="hover:bg-secondary/50 cursor-pointer"
                        onClick={() => setSelectedAsset(asset)}
                        data-testid={`asset-row-${asset.id}`}
                      >
                        <TableCell>
                          <div className={cn("h-3 w-3 rounded-full", getStatusColor(asset.status))} />
                        </TableCell>
                        <TableCell className="font-mono">{asset.hostname}</TableCell>
                        <TableCell className="font-mono text-sm">{asset.ip_addresses[0]}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs uppercase">{asset.type}</span>
                          </div>
                        </TableCell>
                        <TableCell>{asset.vendor}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{asset.model}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{asset.location}</TableCell>
                        <TableCell>
                          {asset.health?.cpu_percent && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs">CPU: {asset.health.cpu_percent}%</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedAsset(asset);
                              }}
                              data-testid={`view-asset-${asset.id}`}
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                            {['router', 'switch', 'firewall'].includes(asset.type) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Abrir Terminal Cisco"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTerminalAsset(asset);
                                }}
                                data-testid={`terminal-asset-${asset.id}`}
                              >
                                <Terminal className="h-4 w-4 text-green-400" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Asset Detail Modal */}
      <Dialog open={!!selectedAsset} onOpenChange={() => setSelectedAsset(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedAsset && (
                <>
                  {React.createElement(typeIcons[selectedAsset.type] || Server, { className: "h-6 w-6 text-primary" })}
                  <span className="font-mono">{selectedAsset.hostname}</span>
                  <div className={cn("h-3 w-3 rounded-full ml-2", getStatusColor(selectedAsset?.status))} />
                  <Badge variant="outline" className="ml-2">{selectedAsset.status?.toUpperCase()}</Badge>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAsset && (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="bg-secondary">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="health">Health</TabsTrigger>
                <TabsTrigger value="network">Network</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
                {['router', 'switch', 'firewall'].includes(selectedAsset.type) && (
                  <TabsTrigger value="terminal" data-testid="terminal-tab">Terminal</TabsTrigger>
                )}
              </TabsList>
              
              <ScrollArea className="h-[500px] mt-4">
                <TabsContent value="overview" className="space-y-4 mt-0">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Informações Básicas</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Hostname</span>
                            <span className="font-mono text-sm">{selectedAsset.hostname}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Tipo</span>
                            <span className="font-mono text-sm uppercase">{selectedAsset.type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Vendor</span>
                            <span className="font-mono text-sm">{selectedAsset.vendor}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Modelo</span>
                            <span className="font-mono text-sm">{selectedAsset.model}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Firmware</span>
                            <span className="font-mono text-sm">{selectedAsset.firmware}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-secondary/50">
                      <CardContent className="pt-4">
                        <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Localização</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Local</span>
                            <span className="font-mono text-sm">{selectedAsset.location || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Descoberto em</span>
                            <span className="font-mono text-sm">{formatDate(selectedAsset.discovered_at)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Último contato</span>
                            <span className="font-mono text-sm">{formatDate(selectedAsset.last_seen)}</span>
                          </div>
                        </div>
                        
                        {selectedAsset.tags?.length > 0 && (
                          <div className="mt-4">
                            <span className="text-sm text-muted-foreground">Tags</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {selectedAsset.tags.map((tag, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
                
                <TabsContent value="health" className="space-y-4 mt-0">
                  {selectedAsset.health && (
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="bg-secondary/50">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Cpu className="h-4 w-4 text-primary" />
                            <span className="font-display text-xs uppercase text-muted-foreground">CPU Usage</span>
                          </div>
                          <div className="text-3xl font-mono font-bold mb-2">{selectedAsset.health.cpu_percent}%</div>
                          <Progress value={selectedAsset.health.cpu_percent} className="h-2" />
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-secondary/50">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <HardDrive className="h-4 w-4 text-primary" />
                            <span className="font-display text-xs uppercase text-muted-foreground">Memory Usage</span>
                          </div>
                          <div className="text-3xl font-mono font-bold mb-2">{selectedAsset.health.memory_percent}%</div>
                          <Progress value={selectedAsset.health.memory_percent} className="h-2" />
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-secondary/50">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Thermometer className="h-4 w-4 text-warning" />
                            <span className="font-display text-xs uppercase text-muted-foreground">Temperature</span>
                          </div>
                          <div className="text-3xl font-mono font-bold">{selectedAsset.health.temperature}°C</div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-secondary/50">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="h-4 w-4 text-success" />
                            <span className="font-display text-xs uppercase text-muted-foreground">Uptime</span>
                          </div>
                          <div className="text-3xl font-mono font-bold">{formatUptime(selectedAsset.health.uptime_seconds)}</div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="network" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Endereços IP</h4>
                      <div className="space-y-2">
                        {selectedAsset.ip_addresses.map((ip, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-background rounded">
                            <Network className="h-4 w-4 text-muted-foreground" />
                            <code className="font-mono text-sm">{ip}</code>
                            {i === 0 && <Badge className="text-xs">Primary</Badge>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Endereços MAC</h4>
                      <div className="space-y-2">
                        {selectedAsset.mac_addresses?.map((mac, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-background rounded">
                            <code className="font-mono text-sm">{mac}</code>
                          </div>
                        )) || <span className="text-muted-foreground text-sm">Nenhum MAC registrado</span>}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="security" className="space-y-4 mt-0">
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Vulnerabilidades</h4>
                      {selectedAsset.vulnerabilities?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedAsset.vulnerabilities.map((vuln, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 bg-destructive/10 rounded border border-destructive/30">
                              <AlertTriangle className="h-4 w-4 text-destructive" />
                              <span className="font-mono text-sm">{vuln}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          <Shield className="h-8 w-8 mx-auto mb-2 text-success" />
                          <p className="text-sm">Nenhuma vulnerabilidade detectada</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3">Compliance</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(selectedAsset.compliance || {}).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between p-2 bg-background rounded">
                            <span className="text-sm uppercase">{key}</span>
                            <Badge variant={value ? "default" : "destructive"}>
                              {value ? "OK" : "FAIL"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {['router', 'switch', 'firewall'].includes(selectedAsset.type) && (
                  <TabsContent value="terminal" className="mt-0">
                    <div className="text-center py-8">
                      <Terminal className="h-12 w-12 mx-auto mb-4 text-green-400" />
                      <p className="font-mono text-green-400 font-bold">Terminal Cisco IOS</p>
                      <p className="text-sm text-muted-foreground mt-1 mb-4">
                        Abra um terminal interativo para investigar este dispositivo
                      </p>
                      <Button
                        className="bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30"
                        onClick={() => {
                          setSelectedAsset(null);
                          setTerminalAsset(selectedAsset);
                        }}
                        data-testid="open-terminal-btn"
                      >
                        <Terminal className="h-4 w-4 mr-2" />
                        Abrir Terminal
                      </Button>
                    </div>
                  </TabsContent>
                )}
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Cisco Terminal */}
      {terminalAsset && (
        <CiscoTerminal device={terminalAsset} onClose={() => setTerminalAsset(null)} />
      )}
    </div>
  );
}

export default AssetsPage;
